import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { WhatsappTemplatesService } from 'src/app/parameters/message-templates/whatsapp-templates/whatsapp-templates.service';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { WhatsappAccountOriginEnum } from '../../shared/models/whatsapp-account-origin.enum';
import { WhatsappCloudApiData } from '../../shared/models/whatsapp-cloud-api-data.model';
import { WhatsappCloudApiLoginEventEnum } from '../../shared/models/whatsapp-cloud-api-login-event.enum';
import { WhatsappCloudApiWarningEnum } from '../../shared/models/whatsapp-cloud-api-warning.enum';
import { WhatsappCloudApiService } from '../../shared/whatsapp-cloud-api.service';

@Component({
  selector: 'app-step-connect-account',
  templateUrl: './step-connect-account.component.html',
  styleUrls: ['./step-connect-account.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class StepConnectAccountComponent implements OnInit, OnDestroy {
  @Input() origin?: WhatsappAccountOriginEnum;
  @Input() migration: boolean;
  @Input() siteId?: number;
  @Input() form: FormGroup;

  @Output() statusChanged: EventEmitter<WhatsappCloudApiLoginEventEnum> = new EventEmitter();

  status: WhatsappCloudApiLoginEventEnum;
  WhatsappCloudApiLoginEventEnum = WhatsappCloudApiLoginEventEnum;
  hostingControl = new FormControl(null, Validators.required);
  regions: Choice[];
  subscriptions = new Subscription();

  constructor(
    public alcmeonContextService: AlcmeonContextService,
    public whatsappCloudApiService: WhatsappCloudApiService,
    private customRouter: CustomRouter,
    private basicService: BasicService,
    private whatsappTemplatesService: WhatsappTemplatesService
  ) {
    whatsappCloudApiService.getRegions().subscribe({
      next: ({ localization_region_codes, default_localization_region_code }) => {
        whatsappCloudApiService.logOperation(
          'get regions',
          localization_region_codes,
          default_localization_region_code
        );
        this.regions = localization_region_codes;
        this.hostingControl.setValue(default_localization_region_code);
      },
    });
  }

  ngOnInit(): void {
    this.hostingControl.setValue(this.form.value.localization);
    if (this.migration) {
      this.subscriptions.add(
        this.form
          .get('migrate')
          .get('agreed')
          .valueChanges.subscribe(value => {
            this.whatsappCloudApiService.logOperation('agreement changed', value);
            if (!value) {
              this.form.get('migrate').get('agreed').setValue(null, { emitEvent: false });
            }
          })
      );
    }
  }

  /**
   * Facebook Login with JavaScript SDK.
   */
  launchWhatsAppSignup() {
    this.whatsappCloudApiService.logOperation('launchWhatsAppSignup');
    this.alcmeonContextService.waCloudApiSettings.data = null;
    this.whatsappCloudApiService.logOperation('reset waCloudApiSettings.data');
    const options = {
      config_id: this.alcmeonContextService.waCloudApiSettings.configId, // configuration ID goes here
      response_type: 'code', // must be set to 'code' for System User access token
      override_default_response_type: true, // when true, any response types passed in the "response_type" will take precedence over the default types
      extras: {
        sessionInfoVersion: 2, //  Receive Session Logging Info
      },
    } as any;
    this.whatsappCloudApiService.logOperation('options', options);
    FB.login(response => {
      this.whatsappCloudApiService.logOperation('FB.login', response);
      FB.logout();
      this.whatsappCloudApiService.logOperation('FB.logout');
      window.removeEventListener('message', sessionInfoListener);
      this.whatsappCloudApiService.logOperation('removeEventListener');
      if (response.authResponse?.code) {
        const { code } = response.authResponse;
        this.whatsappCloudApiService.logOperation('code', code);
        this.basicService.showSpinner();
        const { localization } = this.form.value;
        this.whatsappCloudApiService
          .sessionLogging(code, this.origin, this.siteId, localization)
          .pipe(finalize(() => this.basicService.hideSpinner()))
          .subscribe({
            next: (result: WhatsappCloudApiData) => {
              this.whatsappCloudApiService.logOperation('WhatsappCloudApiData', result);
              this.alcmeonContextService.waCloudApiSettings.data = result;
              if (this.migration) {
                this.whatsappCloudApiService.logOperation('Check limitations in migration mode');
                this.checkLimitations(result.warnings);
              }
              if (!this.status) {
                this.whatsappCloudApiService.logOperation('add account no status', 'FINISH');
                this.statusChanged.emit(WhatsappCloudApiLoginEventEnum.FINISH);
                this.status = WhatsappCloudApiLoginEventEnum.FINISH;
              }
            },
            error: () => {
              this.whatsappCloudApiService.logOperation('add account failed', 'ERROR');
              this.statusChanged.emit(WhatsappCloudApiLoginEventEnum.ERROR);
              this.status = WhatsappCloudApiLoginEventEnum.ERROR;
            },
          });
      } else {
        this.whatsappCloudApiService.logOperation(
          'add account failed',
          'User cancelled login or did not fully authorize'
        );
        this.status = WhatsappCloudApiLoginEventEnum.CANCEL;
        console.warn('User cancelled login or did not fully authorize.');
      }
    }, options);
    const sessionInfoListener = event => {
      if (event.origin !== 'https://www.facebook.com') {
        return;
      }
      try {
        const data = JSON.parse(event.data);
        this.whatsappCloudApiService.logOperation('event received', data.type);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          // if user finishes the Embedded Signup flow
          if (data.event === WhatsappCloudApiLoginEventEnum.FINISH) {
            const { phone_number_id, waba_id } = data.data;
            this.whatsappCloudApiService.logOperation('event FINISH', {
              ...this.alcmeonContextService.waCloudApiSettings.data,
              phone_number_id,
              waba_id,
            });
            this.alcmeonContextService.waCloudApiSettings.data = {
              ...this.alcmeonContextService.waCloudApiSettings.data,
              phone_number_id,
              waba_id,
            };
          } else {
            // if user cancels the Embedded Signup flow
            const { current_step } = data.data;
            this.whatsappCloudApiService.logOperation('event not FINISH > CANCEL', current_step);
            this.whatsappCloudApiService.flowCancelled(current_step).subscribe({
              next: () => {
                this.whatsappCloudApiService.logOperation('reset data');
                this.alcmeonContextService.waCloudApiSettings.data = null;
                this.whatsappCloudApiService.logOperation('redirect to add account page');
                this.customRouter.go('auth.alcmeon.fuse.parameters.add-account');
                this.whatsappCloudApiService.logOperation('set status to CANCEL');
                this.status = WhatsappCloudApiLoginEventEnum.CANCEL;
              },
            });
          }
        }
      } catch {
        // Don’t parse info that’s not a JSON
        console.log('Non JSON Response', event.data);
      }
    };

    window.addEventListener('message', sessionInfoListener);
    this.whatsappCloudApiService.logOperation('addEventListener');
  }

  private checkLimitations(warnings) {
    if (warnings?.length) {
      warnings.forEach(warning => {
        this.whatsappCloudApiService.logOperation('warning found', warning.reason);
        switch (warning.reason) {
          case WhatsappCloudApiWarningEnum.LIMITATIONS:
            warning.descriptionKey =
              'add-account/whatsapp-cloud-api/pre-qualify/steps/payment-line/migration/limitations';
            break;
          case WhatsappCloudApiWarningEnum.LIMITATIONS_WITH_TEMPLATES:
            warning.descriptionKey =
              'add-account/whatsapp-cloud-api/pre-qualify/steps/payment-line/migration/limitations-with-templates';
            break;
          case WhatsappCloudApiWarningEnum.TEMPORARY_LIMITATION:
            warning.descriptionKey =
              'add-account/whatsapp-cloud-api/pre-qualify/steps/payment-line/migration/migration-under-finalisation';
            break;

          default:
            break;
        }
      });

      warnings.forEach(warning => {
        if (warning.reason === WhatsappCloudApiWarningEnum.LIMITATIONS_WITH_TEMPLATES && warning.details?.length) {
          warning.details.forEach(detail => {
            this.whatsappCloudApiService.logOperation('warning detail', detail);
            detail.template$ = this.whatsappTemplatesService.findOne(this.siteId, detail.template_name).pipe(
              map(result => {
                const path = `/#/${this.alcmeonContextService.companyId}/fuse/parameters/message-templates/whatsapp/${this.siteId}/template/${result.metadata.name}`;
                return {
                  ...result,
                  url: result.metadata?.name ? this.basicService.fullURLForRoutePath(path) : undefined,
                };
              })
            );
          });
        }
      });

      this.whatsappCloudApiService.warnings = warnings;

      this.statusChanged.emit(WhatsappCloudApiLoginEventEnum.MIGRATED_WITH_LIMITATIONS);
      this.status = WhatsappCloudApiLoginEventEnum.MIGRATED_WITH_LIMITATIONS;
    }
  }

  validNewLocalization() {
    this.form.get('localization').setValue(this.hostingControl.value);
  }

  clearStep() {
    this.whatsappCloudApiService.logOperation('clear step');
    this.statusChanged.emit(WhatsappCloudApiLoginEventEnum.CANCEL);
    this.whatsappCloudApiService.logOperation('reset status (CANCEL)');
    this.status = null;
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
